import { Helmet } from 'react-helmet-async';
import { useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Stack,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Box,
  Alert,
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Iconify from '../components/iconify';
import * as onboardingService from '../services/onboardingService';

// Stripe publishable key — loaded from env or placeholder for dev
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder'
);

const STEPS = ['Email & Password', 'Select Plan', 'Sensors', 'Confirm', 'Payment'];

const TIERS = [
  {
    value: 'bronze', label: 'Bronze', priceId: 'price_bronze_monthly',
    monthlyPrice: 500, storage: '50 GB', features: [],
    color: '#cd7f32', description: 'Essential plan for getting started.',
  },
  {
    value: 'silver', label: 'Silver', priceId: 'price_silver_monthly',
    monthlyPrice: 1000, storage: '250 GB',
    features: ['AI Transcription', 'AI Analysis'],
    color: '#9e9e9e', description: 'Unlock AI-powered features.',
  },
  {
    value: 'gold', label: 'Gold', priceId: 'price_gold_monthly',
    monthlyPrice: 2500, storage: '1 TB',
    features: ['AI Transcription', 'AI Analysis', 'Advanced AI', 'Priority Processing'],
    color: '#ffd700', description: 'Maximum storage and all features.',
  },
];

const SENSOR_PRICE_CENTS = 5000; // $50 per sensor

function formatPrice(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

// --- Step Components ---

function EmailStep({ email, setEmail, password, setPassword, name, setName, error }) {
  return (
    <Stack spacing={3}>
      <Typography variant="h6">Create your account</Typography>
      <TextField
        label="Full Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        required
      />
      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        fullWidth
        required
        error={Boolean(error)}
        helperText={error}
      />
      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        fullWidth
        required
        helperText="Minimum 8 characters"
      />
    </Stack>
  );
}

function TierStep({ selectedTier, setSelectedTier }) {
  return (
    <Stack spacing={2}>
      <Typography variant="h6">Choose your plan</Typography>
      <Grid container spacing={2}>
        {TIERS.map((tier) => (
          <Grid item xs={12} sm={4} key={tier.value}>
            <Card
              sx={{
                border: selectedTier === tier.value ? `2px solid ${tier.color}` : '2px solid transparent',
                transition: 'border-color 0.2s',
              }}
            >
              <CardActionArea onClick={() => setSelectedTier(tier.value)}>
                <CardContent>
                  <Stack spacing={1} alignItems="center">
                    <Typography variant="h5" sx={{ color: tier.color, fontWeight: 'bold' }}>
                      {tier.label}
                    </Typography>
                    <Typography variant="h4">{formatPrice(tier.monthlyPrice)}</Typography>
                    <Typography variant="body2" color="text.secondary">/month</Typography>
                    <Chip label={tier.storage} size="small" />
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      {tier.description}
                    </Typography>
                    {tier.features.length > 0 && (
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        {tier.features.map((f) => (
                          <Typography key={f} variant="caption" color="text.secondary">
                            + {f}
                          </Typography>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

function SensorStep({ sensors, setSensors }) {
  const addSensor = () => setSensors([...sensors, { name: '', serialNumber: '' }]);
  const removeSensor = (i) => setSensors(sensors.filter((_, idx) => idx !== i));
  const updateSensor = (i, field, value) => {
    const updated = [...sensors];
    updated[i] = { ...updated[i], [field]: value };
    setSensors(updated);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Register sensors (optional)</Typography>
      <Typography variant="body2" color="text.secondary">
        Add any VPSI Air Quality Sensors you purchased. You can skip this step.
      </Typography>
      {sensors.map((sensor, i) => (
        <Stack key={i} direction="row" spacing={2} alignItems="center">
          <TextField
            label="Sensor Name"
            value={sensor.name}
            onChange={(e) => updateSensor(i, 'name', e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Serial Number"
            value={sensor.serialNumber}
            onChange={(e) => updateSensor(i, 'serialNumber', e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <IconButton color="error" onClick={() => removeSensor(i)}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>
      ))}
      <Button variant="outlined" startIcon={<Iconify icon="eva:plus-fill" />} onClick={addSensor}>
        Add Sensor
      </Button>
    </Stack>
  );
}

function ConfirmStep({ name, email, selectedTier, sensors }) {
  const tier = TIERS.find((t) => t.value === selectedTier);
  const sensorTotal = sensors.length * SENSOR_PRICE_CENTS;

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Review your order</Typography>
      <Card variant="outlined">
        <CardContent>
          <List disablePadding>
            <ListItem>
              <ListItemText primary="Name" secondary={name} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Email" secondary={email} />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText primary={`${tier?.label} Plan`} secondary="Monthly subscription" />
              <Typography variant="subtitle1">{formatPrice(tier?.monthlyPrice || 0)}/mo</Typography>
            </ListItem>
            {sensors.length > 0 && (
              <ListItem>
                <ListItemText
                  primary={`${sensors.length} Sensor${sensors.length > 1 ? 's' : ''}`}
                  secondary="One-time charge"
                />
                <Typography variant="subtitle1">{formatPrice(sensorTotal)}</Typography>
              </ListItem>
            )}
            <Divider />
            <ListItem>
              <ListItemText primary="Due today" primaryTypographyProps={{ fontWeight: 'bold' }} />
              <Typography variant="h6">
                {formatPrice((tier?.monthlyPrice || 0) + sensorTotal)}
              </Typography>
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Stack>
  );
}

function PaymentStep({ onSubmit, loading, error }) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardElement);
    const { paymentMethod, error: stripeError } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });
    if (stripeError) {
      onSubmit(null, stripeError.message);
      return;
    }
    onSubmit(paymentMethod.id, null);
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Payment</Typography>
      <Card variant="outlined">
        <CardContent>
          <CardElement
            options={{
              style: {
                base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } },
                invalid: { color: '#9e2146' },
              },
            }}
          />
        </CardContent>
      </Card>
      {error && <Alert severity="error">{error}</Alert>}
      <Button
        variant="contained"
        size="large"
        onClick={handleSubmit}
        disabled={loading || !stripe}
        fullWidth
      >
        {loading ? <CircularProgress size={24} /> : 'Complete Registration'}
      </Button>
    </Stack>
  );
}

function SuccessScreen({ response }) {
  return (
    <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
      <Iconify icon="eva:checkmark-circle-2-fill" width={80} sx={{ color: 'success.main' }} />
      <Typography variant="h4">Welcome to VPSI!</Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center">
        Your account has been created. You can now log in with your email and password.
      </Typography>
      <Card variant="outlined" sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent>
          <List disablePadding>
            <ListItem>
              <ListItemText primary="Email" secondary={response.email} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Plan" secondary={response.tier} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Sensors" secondary={`${response.sensorCount} registered`} />
            </ListItem>
          </List>
        </CardContent>
      </Card>
      <Button variant="contained" href="/login" size="large">
        Go to Login
      </Button>
    </Stack>
  );
}

// --- Main Wizard ---

function OnboardingWizard() {
  const [activeStep, setActiveStep] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [selectedTier, setSelectedTier] = useState('');
  const [sensors, setSensors] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [response, setResponse] = useState(null);

  const canNext = useCallback(() => {
    switch (activeStep) {
      case 0: return name.trim() && email.trim() && password.length >= 8 && !emailError;
      case 1: return Boolean(selectedTier);
      case 2: return true; // sensors optional
      case 3: return true;
      default: return false;
    }
  }, [activeStep, name, email, password, emailError, selectedTier]);

  const handleNext = async () => {
    if (activeStep === 0) {
      // Validate email format
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      setEmailError('');
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handlePayment = async (paymentMethodId, stripeError) => {
    if (stripeError) {
      setPaymentError(stripeError);
      return;
    }

    setPaymentLoading(true);
    setPaymentError('');

    const tier = TIERS.find((t) => t.value === selectedTier);
    try {
      const result = await onboardingService.submitOnboarding({
        email,
        name,
        password,
        tierPriceId: tier.priceId,
        paymentMethodId,
        sensors: sensors.filter((s) => s.name && s.serialNumber),
      });
      setResponse(result);
    } catch (error) {
      const msg = error.response?.data?.message || 'Registration failed. Please try again.';
      setPaymentError(msg);
    } finally {
      setPaymentLoading(false);
    }
  };

  if (response) {
    return <SuccessScreen response={response} />;
  }

  return (
    <Stack spacing={4}>
      <Stepper activeStep={activeStep} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          {activeStep === 0 && (
            <EmailStep
              email={email} setEmail={setEmail}
              password={password} setPassword={setPassword}
              name={name} setName={setName}
              error={emailError}
            />
          )}
          {activeStep === 1 && (
            <TierStep selectedTier={selectedTier} setSelectedTier={setSelectedTier} />
          )}
          {activeStep === 2 && (
            <SensorStep sensors={sensors} setSensors={setSensors} />
          )}
          {activeStep === 3 && (
            <ConfirmStep name={name} email={email} selectedTier={selectedTier} sensors={sensors} />
          )}
          {activeStep === 4 && (
            <PaymentStep onSubmit={handlePayment} loading={paymentLoading} error={paymentError} />
          )}
        </CardContent>
      </Card>

      {activeStep < 4 && (
        <Stack direction="row" justifyContent="space-between">
          <Button disabled={activeStep === 0} onClick={handleBack}>
            Back
          </Button>
          <Button variant="contained" onClick={handleNext} disabled={!canNext()}>
            {activeStep === 3 ? 'Proceed to Payment' : 'Next'}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

export default function OnboardingPage() {
  return (
    <>
      <Helmet>
        <title>Get Started | VPSI</title>
      </Helmet>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box component="img" src="/favicon/favicon-32x32.png" alt="VPSI" sx={{ width: 48, height: 48, mb: 1 }} />
          <Typography variant="h3">Get Started with VPSI</Typography>
          <Typography variant="body1" color="text.secondary">
            Set up your account, choose a plan, and register your sensors.
          </Typography>
        </Box>
        <Elements stripe={stripePromise}>
          <OnboardingWizard />
        </Elements>
      </Container>
    </>
  );
}
